import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { getPromotionBySlug } from '../data/promotionsData';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: var(--gold);
  font-size: 16px;
  cursor: pointer;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &:hover {
    opacity: 0.8;
  }
`;

const BannerSection = styled.div`
  position: relative;
  width: 100%;
  height: 400px;
  border-radius: 18px;
  overflow: hidden;
  margin-bottom: 40px;
  
  @media (max-width: 768px) {
    height: 300px;
  }
`;

const BannerImage = styled.div`
  width: 100%;
  height: 100%;
  background-image: url(${props => props.$image});
  background-size: cover;
  background-position: center;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(to bottom, rgba(0,0,0,0.2), rgba(0,0,0,0.5));
  }
`;

const BannerContent = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 40px;
  z-index: 1;
  color: white;
`;

const Title = styled.h1`
  font-size: 42px;
  font-weight: 700;
  margin-bottom: 12px;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
  
  @media (max-width: 768px) {
    font-size: 32px;
  }
`;

const ShortDescription = styled.p`
  font-size: 18px;
  opacity: 0.95;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.4);
`;

const ContentSection = styled.div`
  background: var(--card);
  border-radius: 18px;
  padding: 40px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  
  @media (max-width: 768px) {
    padding: 24px;
  }
`;

const Description = styled.div`
  font-size: 16px;
  line-height: 1.8;
  color: var(--text);
  
  p {
    margin-bottom: 16px;
  }
  
  h2, h3 {
    margin-top: 24px;
    margin-bottom: 12px;
  }
  
  ul, ol {
    margin-left: 24px;
    margin-bottom: 16px;
  }
`;

const NotFound = styled.div`
  text-align: center;
  padding: 60px 20px;
  font-size: 18px;
  color: #666;
`;

export default function PromotionDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  
  const lang = i18n.language?.startsWith('zh') ? 'zh' : 
               i18n.language?.startsWith('en') ? 'en' : 'ru';
  
  const promotion = getPromotionBySlug(slug, lang);

  if (!promotion) {
    return (
      <Container>
        <BackButton onClick={() => navigate('/promotions')}>
          ← {t('common.back', 'Назад')}
        </BackButton>
        <NotFound>{t('promotions.notFound', 'Акция не найдена')}</NotFound>
      </Container>
    );
  }

  return (
    <Container>
      <BackButton onClick={() => navigate('/promotions')}>
        ← {t('common.back', 'Назад')}
      </BackButton>
      
      <BannerSection>
        <BannerImage $image={promotion.bannerImage} />
        <BannerContent>
          <Title>{promotion.title}</Title>
          {promotion.shortDescription && (
            <ShortDescription>{promotion.shortDescription}</ShortDescription>
          )}
        </BannerContent>
      </BannerSection>
      
      <ContentSection>
        <Description
          dangerouslySetInnerHTML={{
            __html: promotion.description || t('promotions.noDescription', 'Описание скоро появится')
          }}
        />
      </ContentSection>
    </Container>
  );
}
